function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Completion provider for a code session
 */
import { PureComponent } from 'react';
import * as monaco from 'monaco-editor';
import Log from '@deephaven/log';
import init, { Workspace } from '@astral-sh/ruff-wasm-web';
import RUFF_DEFAULT_SETTINGS from "./RuffDefaultSettings.js";
import MonacoUtils from "./MonacoUtils.js";
var log = Log.module('MonacoCompletionProvider');
/**
 * Registers a completion provider with monaco for the language and session provided.
 */
class MonacoProviders extends PureComponent {
  /**
   * Loads and initializes Ruff if it is enabled.
   * Subsequent calls will return the same promise.
   */
  static initRuff() {
    return _asyncToGenerator(function* () {
      if (!MonacoProviders.isRuffEnabled) {
        return;
      }
      if (MonacoProviders.initRuffPromise) {
        return MonacoProviders.initRuffPromise;
      }
      MonacoProviders.initRuffPromise = init({}).then(() => {
        log.debug('Initialized Ruff', Workspace.version());
        MonacoProviders.isRuffInitialized = true;
        MonacoProviders.updateRuffWorkspace();
      });
      return MonacoProviders.initRuffPromise;
    })();
  }

  /**
   * Updates the current ruff workspace with MonacoProviders.ruffSettings.
   * Re-lints all Python models after updating.
   */
  static updateRuffWorkspace() {
    if (!MonacoProviders.isRuffInitialized) {
      return;
    }

    /* eslint-disable no-console */
    var prevLog = console.log;
    try {
      console.log = () => undefined; // Suppress not useful ruff-wasm-web logs when it creates the workspace
      MonacoProviders.ruffWorkspace = new Workspace(MonacoProviders.ruffSettings);
    } finally {
      console.log = prevLog; // Restore console.log
    }
    /* eslint-enable no-console */

    MonacoProviders.lintAllPython();
  }

  /**
   * Sets ruff settings
   * @param settings The ruff settings
   */
  static setRuffSettings() {
    var settings = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : MonacoProviders.ruffSettings;
    MonacoProviders.ruffSettings = settings;
    if (!MonacoProviders.isRuffInitialized) {
      MonacoProviders.initRuff();
      return;
    }
    MonacoProviders.updateRuffWorkspace();
  }
  static getDiagnostics(model) {
    if (!MonacoProviders.ruffWorkspace) {
      return [];
    }
    var diagnostics = MonacoProviders.ruffWorkspace.check(model.getValue());
    if (MonacoUtils.isConsoleModel(model)) {
      // Only want SyntaxErrors for console which have no code
      return diagnostics.filter(d => d.code == null);
    }
    return diagnostics;
  }
  static lintAllPython() {
    if (!MonacoProviders.isRuffEnabled) {
      monaco.editor.removeAllMarkers('ruff');
      return;
    }
    monaco.editor.getModels().filter(m => m.getLanguageId() === 'python').forEach(MonacoProviders.lintPython);
  }
  static lintPython(model) {
    if (!MonacoProviders.isRuffEnabled) {
      return;
    }
    if (!MonacoProviders.ruffWorkspace) {
      return;
    }
    var diagnostics = MonacoProviders.getDiagnostics(model);
    log.debug("Linting Python document: ".concat(model.uri.toString()), diagnostics);
    monaco.editor.setModelMarkers(model, 'ruff', diagnostics.map(d => {
      // Unused variable or import. Mark as unnecessary to fade the text
      var isUnnecessary = d.code === 'F401' || d.code === 'F841';
      var isSyntaxError = d.code == null; // SyntaxError has no error code
      return {
        startLineNumber: d.location.row,
        startColumn: d.location.column,
        endLineNumber: d.end_location.row,
        endColumn: d.end_location.column,
        message: isSyntaxError ? d.message : "".concat(d.code, ": ").concat(d.message),
        severity: isSyntaxError ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
        tags: isUnnecessary ? [monaco.MarkerTag.Unnecessary] : []
      };
    }));
  }

  /**
   * Converts LSP CompletionItemKind to Monaco CompletionItemKind
   * Defaults to Variable if no LSP kind was provided
   * https://microsoft.github.io/language-server-protocol/specifications/lsp/3.17/specification/#completionItemKind
   *
   * @param kind The LSP kind
   * @returns Monaco kind
   */
  static lspToMonacoKind(kind) {
    var monacoKinds = monaco.languages.CompletionItemKind;
    switch (kind) {
      case 1:
        return monacoKinds.Text;
      case 2:
        return monacoKinds.Method;
      case 3:
        return monacoKinds.Function;
      case 4:
        return monacoKinds.Constructor;
      case 5:
        return monacoKinds.Field;
      case 6:
        return monacoKinds.Variable;
      case 7:
        return monacoKinds.Class;
      case 8:
        return monacoKinds.Interface;
      case 9:
        return monacoKinds.Module;
      case 10:
        return monacoKinds.Property;
      case 11:
        return monacoKinds.Unit;
      case 12:
        return monacoKinds.Value;
      case 13:
        return monacoKinds.Enum;
      case 14:
        return monacoKinds.Keyword;
      case 15:
        return monacoKinds.Snippet;
      case 16:
        return monacoKinds.Color;
      case 17:
        return monacoKinds.File;
      case 18:
        return monacoKinds.Reference;
      case 19:
        return monacoKinds.Folder;
      case 20:
        return monacoKinds.EnumMember;
      case 21:
        return monacoKinds.Constant;
      case 22:
        return monacoKinds.Struct;
      case 23:
        return monacoKinds.Event;
      case 24:
        return monacoKinds.Operator;
      case 25:
        return monacoKinds.TypeParameter;
      default:
        return monacoKinds.Variable;
    }
  }

  /**
   * Converts an LSP document range to a monaco range
   * Accounts for LSP indexing from 0 and monaco indexing from 1
   *
   * @param range The LSP document range to convert
   * @returns The corresponding monaco range
   */
  static lspToMonacoRange(range) {
    var start = range.start,
      end = range.end;

    // Monaco expects the columns/ranges to start at 1. LSP starts at 0
    return {
      startLineNumber: start.line + 1,
      startColumn: start.character + 1,
      endLineNumber: end.line + 1,
      endColumn: end.character + 1
    };
  }

  /**
   * Converts a monaco position to an LSP position
   * Accounts for LSP indexing from 0 and monaco indexing from 1
   *
   * @param position The monaco position
   * @returns The corresponding LSP position
   */
  static monacoToLspPosition(position) {
    // Monaco 1-indexes Position. LSP 0-indexes Position
    return {
      line: position.lineNumber - 1,
      character: position.column - 1
    };
  }
  static handlePythonCodeActionRequest(model, range) {
    if (!MonacoProviders.isRuffEnabled || !MonacoProviders.ruffWorkspace) {
      return {
        actions: [],
        dispose: () => {
          /* no-op */
        }
      };
    }
    var diagnostics = MonacoProviders.getDiagnostics(model).filter(d => {
      var diagnosticRange = new monaco.Range(d.location.row, d.location.column, d.end_location.row, d.end_location.column);
      return d.code != null &&
      // Syntax errors have no code and can't be fixed/disabled
      diagnosticRange.intersectRanges(range);
    });
    var fixActions = diagnostics.filter(_ref => {
      var fix = _ref.fix;
      return fix != null;
    }).map(d => {
      var title = 'Fix';
      if (d.fix != null) {
        if (d.fix.message != null && d.fix.message !== '') {
          title = "".concat(d.code, ": ").concat(d.fix.message);
        } else {
          title = "Fix ".concat(d.code);
        }
      }
      return {
        title,
        id: "fix-".concat(d.code),
        kind: 'quickfix',
        edit: d.fix ? {
          edits: d.fix.edits.map(edit => {
            var _edit$content;
            return {
              resource: model.uri,
              versionId: model.getVersionId(),
              textEdit: {
                range: {
                  startLineNumber: edit.location.row,
                  startColumn: edit.location.column,
                  endLineNumber: edit.end_location.row,
                  endColumn: edit.end_location.column
                },
                text: (_edit$content = edit.content) !== null && _edit$content !== void 0 ? _edit$content : ''
              }
            };
          })
        } : undefined
      };
    });
    var seenCodes = new Set();
    var duplicateCodes = new Set();
    diagnostics.forEach(d => {
      if (d.code == null) {
        return;
      }
      if (seenCodes.has(d.code)) {
        duplicateCodes.add(d.code);
      }
      seenCodes.add(d.code);
    });
    var disableLineActions = diagnostics.map(d => {
      if (d.code == null) {
        // The nulls are already filtered out, but TS doesn't know that
        return [];
      }
      var line = model.getLineContent(d.location.row);
      var lastToken = monaco.editor.tokenize(line, model.getLanguageId())[0].at(-1);
      var lineEdit = {
        range: {
          startLineNumber: d.location.row,
          startColumn: line.length + 1,
          endLineNumber: d.location.row,
          endColumn: line.length + 1
        },
        text: " # noqa: ".concat(d.code)
      };
      if (lastToken != null && lastToken.type.startsWith('comment')) {
        // Already a comment at the end of the line
        lineEdit.text = "# noqa: ".concat(d.code, " ");
        if (line.startsWith('# noqa:', lastToken.offset)) {
          // Already another suppressed rule on the line
          lineEdit.range.startColumn = lastToken.offset + 1;
          lineEdit.range.endColumn = lastToken.offset + 9; // "# noqa: " length + 1 to offset
        } else {
          lineEdit.range.startColumn = lastToken.offset + 1;
          lineEdit.range.endColumn = line.startsWith('# ', lastToken.offset) ? lastToken.offset + 3 // "# " + 1 to offset
          : lastToken.offset + 2; // "#" + 1 to offset
        }
      }
      return [{
        title: "Disable ".concat(d.code, " for ").concat(duplicateCodes.has(d.code) ? "line ".concat(d.location.row) : 'this line'),
        kind: 'quickfix',
        edit: {
          edits: [{
            resource: model.uri,
            versionId: model.getVersionId(),
            textEdit: lineEdit
          }]
        }
      }];
    }).flat().filter(
    // Remove actions with duplicate titles as you can't disable the same rule on a line twice
    (action, i, arr) => arr.find(a => a.title === action.title) === action);
    var disableGlobalActions = [...seenCodes].map(code => ({
      title: "Disable ".concat(code, " for this file"),
      kind: 'quickfix',
      edit: {
        edits: [{
          resource: model.uri,
          versionId: model.getVersionId(),
          textEdit: {
            range: {
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: 1,
              endColumn: 1
            },
            text: "# ruff: noqa: ".concat(code, "\n")
          }
        }]
      }
    }));
    return {
      actions: [...fixActions, ...disableLineActions, ...disableGlobalActions],
      dispose: () => {
        /* no-op */
      }
    };
  }
  static handlePythonFormatRequest(model, options, token) {
    if (!MonacoProviders.ruffWorkspace) {
      return;
    }
    log.debug("Formatting Python document: ".concat(model.uri.toString));
    return [{
      range: model.getFullModelRange(),
      text: MonacoProviders.ruffWorkspace.format(model.getValue())
    }];
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "registeredCompletionProvider", void 0);
    _defineProperty(this, "registeredSignatureProvider", void 0);
    _defineProperty(this, "registeredHoverProvider", void 0);
    this.handleCompletionRequest = this.handleCompletionRequest.bind(this);
    this.handleSignatureRequest = this.handleSignatureRequest.bind(this);
    this.handleHoverRequest = this.handleHoverRequest.bind(this);
  }
  componentDidMount() {
    var _this$props = this.props,
      language = _this$props.language,
      session = _this$props.session;
    this.registeredCompletionProvider = monaco.languages.registerCompletionItemProvider(language, {
      provideCompletionItems: this.handleCompletionRequest,
      triggerCharacters: ['.', '"', "'"]
    });
    if (session.getSignatureHelp != null) {
      this.registeredSignatureProvider = monaco.languages.registerSignatureHelpProvider(language, {
        provideSignatureHelp: this.handleSignatureRequest,
        signatureHelpTriggerCharacters: ['(', ',']
      });
    }
    if (session.getHover != null) {
      this.registeredHoverProvider = monaco.languages.registerHoverProvider(language, {
        provideHover: this.handleHoverRequest
      });
    }
  }
  componentWillUnmount() {
    var _this$registeredCompl, _this$registeredSigna, _this$registeredHover;
    (_this$registeredCompl = this.registeredCompletionProvider) === null || _this$registeredCompl === void 0 || _this$registeredCompl.dispose();
    (_this$registeredSigna = this.registeredSignatureProvider) === null || _this$registeredSigna === void 0 || _this$registeredSigna.dispose();
    (_this$registeredHover = this.registeredHoverProvider) === null || _this$registeredHover === void 0 || _this$registeredHover.dispose();
  }
  handleCompletionRequest(model, position, context) {
    var _this$props2 = this.props,
      propModel = _this$props2.model,
      session = _this$props2.session;
    if (model !== propModel) {
      return null;
    }
    var params = {
      textDocument: {
        uri: "".concat(model.uri),
        version: model.getVersionId()
      },
      position: MonacoProviders.monacoToLspPosition(position),
      context
    };
    var completionItems = session.getCompletionItems(params);
    log.debug('Requested completion items', params);
    var monacoCompletionItems = completionItems.then(items => {
      log.debug('Completion items received: ', params, items);
      var suggestions = items.map(item => {
        var label = item.label,
          kind = item.kind,
          detail = item.detail,
          documentation = item.documentation,
          sortText = item.sortText,
          filterText = item.filterText,
          textEdit = item.textEdit,
          insertTextFormat = item.insertTextFormat;
        return {
          label,
          kind: MonacoProviders.lspToMonacoKind(kind),
          detail,
          documentation: (documentation === null || documentation === void 0 ? void 0 : documentation.kind) === 'markdown' ? documentation : documentation === null || documentation === void 0 ? void 0 : documentation.value,
          sortText,
          filterText,
          insertText: textEdit.text,
          // We are basically guessing that LSP's insertTextFormat===2 is
          // semantically equivalent to monaco's insertTextRules===4.
          // Why microsoft is using almost-but-not-LSP apis is beyond me....
          insertTextRules: insertTextFormat === 2 ? 4 : insertTextFormat,
          range: MonacoProviders.lspToMonacoRange(textEdit.range)
        };
      });
      return {
        incomplete: true,
        suggestions
      };
    }).catch(error => {
      log.error('There was an error retrieving completion items', error);
      return {
        suggestions: []
      };
    });
    return monacoCompletionItems;
  }
  handleSignatureRequest(model, position, token, context) {
    var defaultResult = {
      value: {
        signatures: [],
        activeSignature: 0,
        activeParameter: 0
      },
      dispose: () => {
        /* no-op */
      }
    };
    var _this$props3 = this.props,
      propModel = _this$props3.model,
      session = _this$props3.session;
    if (model !== propModel || session.getSignatureHelp == null) {
      return null;
    }
    var params = {
      textDocument: {
        uri: "".concat(model.uri),
        version: model.getVersionId()
      },
      position: MonacoProviders.monacoToLspPosition(position),
      context
    };
    var signatureItems = session.getSignatureHelp(params);
    log.debug('Requested signature help', params);
    var monacoSignatures = signatureItems.then(items => {
      log.debug('Signatures received: ', params, signatureItems);
      var signatures = items.map(item => {
        var label = item.label,
          documentation = item.documentation,
          parameters = item.parameters;
        return {
          documentation: (documentation === null || documentation === void 0 ? void 0 : documentation.kind) === 'markdown' ? documentation : documentation === null || documentation === void 0 ? void 0 : documentation.value,
          label,
          parameters: parameters !== null && parameters !== void 0 ? parameters : []
        };
      });
      if (signatures.length === 0) {
        return defaultResult;
      }

      // For now we will assume we only autocomplete Python w/ 1 signature
      // For multiple signatures, this may need to be sent through the request as context
      var activeSignature = 0;
      return {
        value: {
          signatures,
          activeSignature,
          activeParameter: items[activeSignature].activeParameter
        },
        dispose: () => {
          /* no-op */
        }
      };
    }).catch(error => {
      log.error('There was an error retrieving completion items', error);
      return defaultResult;
    });
    return monacoSignatures;
  }
  handleHoverRequest(model, position) {
    var _this$props4 = this.props,
      propModel = _this$props4.model,
      session = _this$props4.session;
    if (model !== propModel || session.getHover == null) {
      return null;
    }
    var params = {
      textDocument: {
        uri: "".concat(model.uri),
        version: model.getVersionId()
      },
      position: MonacoProviders.monacoToLspPosition(position)
    };
    var hover = session.getHover(params);
    log.debug('Requested hover', params);
    var monacoHover = hover.then(hoverItem => {
      log.debug('Hover received: ', params, hoverItem);
      var hoverContents = hoverItem.contents;
      return {
        contents: hoverContents != null ? [hoverContents] : []
      };
    }).catch(error => {
      log.error('There was an error retrieving hover', error);
      return {
        contents: []
      };
    });
    return monacoHover;
  }
  render() {
    return null;
  }
}
_defineProperty(MonacoProviders, "ruffWorkspace", void 0);
_defineProperty(MonacoProviders, "initRuffPromise", void 0);
_defineProperty(MonacoProviders, "isRuffInitialized", false);
_defineProperty(MonacoProviders, "isRuffEnabled", true);
_defineProperty(MonacoProviders, "ruffSettings", RUFF_DEFAULT_SETTINGS);
export default MonacoProviders;
//# sourceMappingURL=MonacoProviders.js.map